import { useIntl } from "react-intl";
import { Asset } from "../../../models/asset";
import { Scheduling } from "../../../models/schedule";
import { SignedRevision } from "../../../models/signedRevision";
import useAssets from "../../../hooks/useAssets";

interface Blockchain {
  transactionDate: string;
  transactionHash: string;
}

interface IPeriodicReviewsPdfProps {
  asset: Asset | null;
  blockchainData: (scheduleId: string) => SignedRevision | undefined;
  order: string;
  paginateSchedulings: (schedulings: Scheduling[]) => Scheduling[][];
}

const PeriodicReviewsPdf = ({
  asset,
  blockchainData,
  order,
  paginateSchedulings,
}: IPeriodicReviewsPdfProps) => {
  const intl = useIntl();
  const { getAssetById } = useAssets();
  const summaryAsset = asset?.id ? getAssetById(asset.id) : undefined;
  const displayName = summaryAsset?.name && summaryAsset.name !== summaryAsset.chassis ? summaryAsset.name : undefined;
  const displayPlate = summaryAsset?.licensePlate && summaryAsset.licensePlate !== summaryAsset.chassis ? summaryAsset.licensePlate : undefined;
  
  /**
   * Extrai o número do título (ex: "1º" -> 1, "37º" -> 37)
   */
  const extractNumber = (title: string): number => {
    const match = title.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  /**
   * Ordena os agendamentos com os 3 principais sempre em primeiro
   * Os outros são ordenados pelo número do título (1º, 2º, ... 37º)
   * @param schedulings Array de Scheduling
   * @returns Array ordenado com os 3 principais em primeiro
   */
  const sortSchedulings = (schedulings: Scheduling[]): Scheduling[] => {
    const mainTypes = [
      intl.formatMessage({ id: "intl-msg:periodicReviews.technicalDelivery" }),
      intl.formatMessage({ id: "intl-msg:periodicReviews.deliveryReview" }),
      intl.formatMessage({ id: "intl-msg:periodicReviews.settling" })
    ];
    
    const main = schedulings.filter(s => mainTypes.includes(s.checkup?.name || ""));
    const others = schedulings.filter(s => !mainTypes.includes(s.checkup?.name || ""));
    
    const sortedMain = mainTypes
      .map(type => main.find(s => s.checkup?.name === type))
      .filter((item): item is Scheduling => item !== undefined);
    
    const sortedOthers = others.sort((a, b) => {
      const numA = extractNumber(a.checkup?.name || "");
      const numB = extractNumber(b.checkup?.name || "");
      
      return order === "down" ? numA - numB : numB - numA;
    });
    
    return [...sortedMain, ...sortedOthers];
  };

  return (
    <div
      className="revision-content px-20 max-w-[85%] mx-auto"
      style={{ top: "-10px" }}
      id="periodic-reviews-pdf"
    >
      {paginateSchedulings(sortSchedulings([
        ...(asset?.schedulings?.previous ?? []),
        ...(asset?.schedulings?.next ?? []),
      ])).map((schedulings, index) => (
        <div key={index} className={`h-[1040px] mt-[10px] overflow-hidden`}>
          {/* Header do veículo em todas as páginas */}
          <div className="flex justify-between items-center border-b border-color-light pb-3 mb-4">
            <div className="flex flex-col gap-2">
              <h3 className="flex gap-4 m-0 text-size-16">
                <span className="rioglyph rioglyph-book text-size-16"></span>
                {intl.formatMessage({ id: "intl-msg:periodicReviews.maintenanceAndReviews" })}
              </h3>
              <div className="flex gap-6 flex-wrap">
                <span className="text-size-12 text-color-dark font-semibold">
                  {displayName ?? "-"}
                </span>
                <span className="text-size-12 text-color-dark">
                  <span className="text-color-gray">Chassi:&nbsp;</span>{asset?.chassis || "-"}
                </span>
                <span className="text-size-12 text-color-dark">
                  <span className="text-color-gray">Placa:&nbsp;</span>{displayPlate ?? "-"}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-4">
              {/* <img src="/assets/images/rio-icon.svg" alt="Rio icon" /> */}
              {paginateSchedulings(sortSchedulings([
                ...(asset?.schedulings?.previous ?? []),
                ...(asset?.schedulings?.next ?? []),
              ])).length > 1 && (
                <span className="text-size-10 text-color-gray">
                  {index + 1} / {paginateSchedulings(sortSchedulings([
                    ...(asset?.schedulings?.previous ?? []),
                    ...(asset?.schedulings?.next ?? []),
                  ])).length}
                </span>
              )}
            </div>
          </div>
          {index === 0 && (
            <div className="mb-3">
              <span className="text-size-12">{intl.formatMessage({ id: "intl-msg:periodicReviews.reviewsByOrder" })}</span>
            </div>
          )}

          <div className="grid grid-cols-2 grid-rows-2 gap-4 mt-2" style={{ height: '880px' }}>
            {schedulings.map((scheduleCheckup: Scheduling, index: number) => {
              const blockchain = blockchainData(scheduleCheckup.id);
              return (
                <Card
                  scheduling={scheduleCheckup}
                  isSigned={!!blockchain?.id}
                  finished={scheduleCheckup.isFinished}
                  blockchain={{
                    transactionDate:
                      (typeof blockchain?.reviewDate === "string"
                        ? new Date(blockchain?.reviewDate).toLocaleDateString(
                          "pt-BR"
                        )
                        : blockchain?.reviewDate?.toLocaleDateString(
                          "pt-BR"
                        )) ?? "",
                    transactionHash: blockchain?.id ?? "",
                  }}
                  intl={intl}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

interface ICardProps {
  scheduling: Scheduling;
  isSigned?: boolean;
  finished?: boolean;
  blockchain: Blockchain;
  intl: any;
}
const Card = ({ scheduling, isSigned, finished, blockchain, intl }: ICardProps) => {
  return (
    <div className={`border border-width-2 rounded-lg revision-card flex flex-col h-full overflow-hidden`}>
      <div
        className={`flex justify-between items-center p-2 px-3 border-color-light border-b`}
      >
        <p className="p-0 m-0 truncate text-size-12" style={{ padding: '4px 0' }}>
          {scheduling.checkup?.name}
        </p>
        <span
          className={`rioglyph rioglyph-eye-option ${scheduling.checkup?.services?.length
              ? "cursor-pointer text-color-primary"
              : "cursor-not-allowed"
            }`}
        />
      </div>

      <>
        <div
          className={`flex justify-between gap-2 px-3 py-2 border-color-light border-b`}
        >
          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.osNumber" })}</label>
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling?.os : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
          <div className={`form-group w-full`} style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.date" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.date?.toLocaleDateString("pt-BR")
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
        </div>

        <div
          className={`flex justify-between gap-2 px-3 py-2 border-color-light border-b`}
        >
          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.hourmeter" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.hourmeter?.toLocaleString("pt-Br")
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>

          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.km" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.odometer?.toLocaleString("pt-Br")
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
        </div>

        <div className="form-group flex flex-wrap pl-2 py-1 field-section border-color-light border-b" style={{ marginBottom: 0 }}>
          {[
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.settling" }),
              value: "Assentamento",
            },
            {
              label: "L",
              value: "L",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp1" }),
              value: "MP1",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp2" }),
              value: "MP2",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.intermediate" }),
              value: "Intermediária"
            },
          ].map((item) => (
            <div
              key={item.value}
              className="text-left mr-3 my-1 flex items-center"
            >
              <div
                className={`w-3 h-3 border border-gray-400 rounded-sm mr-2 flex items-center justify-center ${scheduling.checkup?.name === item.value
                    ? "bg-primary border-color-info"
                    : "bg-white"
                  }`}
                style={{
                  minWidth: "12px",
                  minHeight: "12px",
                }}
              >
                {scheduling.checkup?.name === item.value && (
                  <svg
                    width="8"
                    height="6"
                    viewBox="0 0 8 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 2.5L3 4.5L7 0.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="text-size-10 text-color-dark">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="form-group flex flex-col justify-between px-3 py-2 field-section flex-1 overflow-hidden" style={{ marginBottom: 0 }}>
          <label className="block w-full text-size-11 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.dealership" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.dealership?.name : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </label>
          <label className="block w-full text-size-11 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.consultant" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.consultant : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </label>
        </div>

        <div className="flex items-center font-semibold text-color-primary text-teal-600 footer-card p-1 border-color-light border-t" style={{ minHeight: '36px' }}>
          {finished && !isSigned ? (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full pt-5`}
            >
              <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.completed" })}</span>
              <img src="/assets/images/check-icon.svg" alt="Check icon" />
            </div>
          ) : isSigned ? (
            <div className="w-full">
              <div
                className={`text-size-10 flex items-center gap-8 justify-between w-full`}
              >
                <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.authenticated" })}</span>
                <img
                  src="/assets/images/blockchain-enabled.svg"
                  alt="Enabled Blockchain icon"
                />
              </div>
              <div
                className={`text-size-10 w-full rounded-md bg-[#F0FEFF] p-2 mt-2`}
              >
                <div className="flex items-center justify-between text-size-10">
                  <div>Transcation ID:</div>
                  <div>{blockchain.transactionDate}</div>
                </div>
                <div
                  className="text-size-10 break-all"
                  style={{ wordBreak: "break-all", maxWidth: 350 }}
                >
                  {blockchain.transactionHash}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full text-color-gray pt-5`}
            >
              <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.awaitingAuth" })}</span>
              <img
                src="/assets/images/blockchain-disabled.svg"
                alt="Disabled Blockchain icon"
              />
            </div>
          )}
        </div>
      </>
    </div>
  );
};

export default PeriodicReviewsPdf;
